// Taken from https://github.com/buildo/react-autosize-textarea/blob/2d5fbc381edf30aaeecca5bc01a0ecc7b8e495d1/src/TextareaAutosize.js (MIT license)
// 50KB of tcomb validation requirement has been removed

import './styles.scss'

import React, { useEffect, useRef, forwardRef } from 'react'
import PropTypes from 'prop-types'
import autosize from 'autosize'

import { normalizeString } from 'lib/utils/normalize-string'

const UPDATE = 'autosize:update'
const DESTROY = 'autosize:destroy'
const RESIZED = 'autosize:resized'

const TextareaAutosize = forwardRef(
  (
    {
      value = '',
      neverFocus = false,
      onResize,
      onValueChange,
      onClick,
      onChange,
      focusOnMount = false,
      children,
      autosize: shouldAutosize = true,
      placeholder,
      style = {},
      ...props
    },
    ref
  ) => {
    const textareaRef = useRef(null)

    const dispatchEvent = (element, eventType, defer = false) => {
      const event = new Event(eventType, { bubbles: true, cancelable: false })
      const dispatch = () => element.dispatchEvent(event)
      defer ? setTimeout(dispatch) : dispatch()
    }

    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(textareaRef.current)
        } else {
          ref.current = textareaRef.current
        }
      }
    }, [ref])

    useEffect(() => {
      const textarea = textareaRef.current
      if (textarea) {
        textarea.value = value
      }

      if (shouldAutosize && textarea) {
        autosize(textarea)
      }

      if (onResize && textarea) {
        textarea.addEventListener(RESIZED, onResize)
      }

      if (focusOnMount && textarea) {
        textarea.focus()
      }

      return () => {
        if (onResize) {
          textarea.removeEventListener(RESIZED, onResize)
        }

        dispatchEvent(textarea, DESTROY)
      }
    }, [onResize, shouldAutosize, focusOnMount, value])

    useEffect(() => {
      if (textareaRef.current && value !== textareaRef.current.value) {
        dispatchEvent(textareaRef.current, UPDATE, true)
      }
    }, [value])

    const handleChange = event => {
      onChange && onChange(event)
      onValueChange && onValueChange(normalizeString(event.target.value))
    }

    const handleFocus = () => {
      if (textareaRef.current) {
        textareaRef.current.focus()
      }
    }

    const handleClick = event => {
      if (neverFocus && textareaRef.current) {
        event.preventDefault()
        textareaRef.current.blur()
      }

      onClick && onClick(event)
    }

    const showReactPlaceholder = placeholder && typeof placeholder !== 'string' && !value

    return (
      <div style={{ ...style, position: 'relative' }} className='watermark_container'>
        {showReactPlaceholder && (
          <div
            {...props}
            className='textarea-placeholder'
            onClick={handleFocus}
            style={{
              position: 'inherit',
              left: 0,
              top: 0,
              pointerEvents: 'none',
              background: 'transparent',
              ...style
            }}
          >
            {placeholder}
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={value}
          placeholder={!showReactPlaceholder && typeof placeholder === 'string' ? placeholder : ''}
          className={`textarea-aligned${shouldAutosize ? '' : ' no-autosize'}`}
          {...props}
          onClick={handleClick}
          style={
            showReactPlaceholder
              ? {
                  position: 'absolute',
                  minHeight: '100%',
                  left: 0,
                  top: 0,
                  background: 'transparent',
                  resize: 'none',
                  ...style
                }
              : {
                  minHeight: style.minHeight || 55,
                  resize: 'none',
                  ...style
                }
          }
          onChange={handleChange}
        >
          {children}
        </textarea>
      </div>
    )
  }
)

TextareaAutosize.displayName = 'TextareaAutosize'

TextareaAutosize.propTypes = {
  value: PropTypes.string,
  neverFocus: PropTypes.bool,
  onResize: PropTypes.func,
  onValueChange: PropTypes.func,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  focusOnMount: PropTypes.bool,
  children: PropTypes.node,
  autosize: PropTypes.bool,
  placeholder: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  style: PropTypes.object
}

export default TextareaAutosize
